import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { apiClient } from "api";
import { AppState } from "store/types";
import { useDispatch, useSelector } from "react-redux";
import SellersList from "./components/SellersList";
import { addRecords } from "store/catalogTracker/catalogTracker.slice";
import { useIntl } from "react-intl";
import CatalogTrackerTable from "components/Table/CatalogTrackerTable";
import I18nKey from "translations/I18nKey";
import CatalogTrackerHeader from "./CatalogTrackerHeader";
import CatalogAttributesTable from "components/Table/CatalogAttributesTable";
import {
  BottomContent,
  CatalogTrackerContentWrapper,
  CatalogTrackerTabsWrapper,
  CatalogTrackerWrapper,
  TabsStyle,
  TabStyle,
} from "./style";
import { isNull } from "lodash";
import Button from "components/Button";
import {
  DropdownLabelStyle,
  FormControlStyle,
  SelectStyle,
} from "components/Table/SelectSellerModal/style";

const CatalogTracker = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const filters = useSelector((state: AppState) => state.table.columnFilters);
  const records = useSelector(
    (state: AppState) => state.catalogTracker.records
  );
  const source = useSelector((state: AppState) => state.catalogTracker.source);
  const selectedSellers = useSelector(
    (state: AppState) => state.auth.selectedSellers
  );

  const [mismatchSummaryData, setMismatchSummaryData] = useState<any[]>([]);
  const [columnName, setColumnName] = useState<string>("forecast");
  const [sortType, setSortType] = useState<string>("desc");
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [attributesAsin, setAttributesAsin] = useState<string>("");
  const [seller, setSeller] = useState<string>("");
  const [value, setValue] = useState<number>(0);
  const [attributeData, setAttributeData] = useState<any>({});

  const getData = async () => {
    return await apiClient.getMismatchSummary(
      pageSize,
      pageNumber,
      columnName === "mismatch_count"
        ? [[`${source}_mismatch_count`, sortType]]
        : [
            [`${source}_mismatch_count`, sortType],
            [columnName, sortType],
          ],
      filters,
      source
    );
  };

  const { data, isFetching, refetch } = useQuery(
    [
      "mismatchSummaryData",
      columnName,
      sortType,
      pageSize,
      filters,
      pageNumber,
      source,
    ],
    getData,
    { refetchOnWindowFocus: false }
  );

  const getAttributeData = async () => {
    return await apiClient
      .getCatalogAttributes(seller, attributesAsin)
      .then((res) => {
        setAttributeData(res);
      });
  };

  const {
    data: attribute,
    isFetching: isFetchingCatalogAttributes,
    refetch: attributeRefetch,
    error: catalogAttributesError,
  } = useQuery("catalogAttributesData", getAttributeData, {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (source) {
      refetch();
    }
  }, [source]);

  useEffect(() => {
    setSeller("");
    setAttributesAsin("");
  }, []);

  const getColumnToSort = (col: string, order: string) => {
    const colName = () => {
      if (col === "mismatch") {
        return "mismatch_count";
      }
      return col;
    };
    setColumnName(colName());
    setSortType(order);
    setPageNumber(1);
  };

  useEffect(() => {
    if (filters?.length && filters[1].length) {
      setPageNumber(1);
    }
  }, [filters]);

  useEffect(() => {
    if (data?.data) {
      setMismatchSummaryData(
        data?.data?.records?.map((item: any, id: number) => ({
          id,
          asin: item?.asin,
          seller_name: item?.seller_name,
          title: isNull(item.title)
            ? "-"
            : item.title === false
            ? " ✅ "
            : "❌",
          brand: isNull(item.brand)
            ? "-"
            : item.brand === false
            ? " ✅ "
            : "❌",
          bullet_point_1: isNull(item.bullet_points?.bullet_points_1)
            ? "-"
            : item.bullet_points?.bullet_points_1 === false
            ? " ✅ "
            : " ❌ ",
          bullet_point_2: isNull(item.bullet_points?.bullet_points_2)
            ? "-"
            : item.bullet_points?.bullet_points_2 === false
            ? " ✅ "
            : " ❌ ",
          bullet_point_3: isNull(item.bullet_points?.bullet_points_3)
            ? "-"
            : item.bullet_points?.bullet_points_3 === false
            ? " ✅ "
            : " ❌ ",
          bullet_point_4: isNull(item.bullet_points?.bullet_points_4)
            ? "-"
            : item.bullet_points?.bullet_points_4 === false
            ? " ✅ "
            : " ❌ ",
          bullet_point_5: isNull(item.bullet_points?.bullet_points_5)
            ? "-"
            : item.bullet_points?.bullet_points_5 === false
            ? " ✅ "
            : " ❌ ",
          description: isNull(item.description)
            ? "-"
            : item.description === false
            ? " ✅ "
            : " ❌ ",
          price: isNull(item.price)
            ? "-"
            : item.price === false
            ? " ✅ "
            : " ❌ ",
          mismatch: item.mismatch_count === 0 ? "0" : item.mismatch_count,
          forecast: item?.forecast
            ? Intl.NumberFormat("en-US").format(
                Number(item?.forecast?.toFixed(0))
              )
            : "-",
        }))
      );
      dispatch(addRecords(data?.total_records));
    }
  }, [data, records]);

  const getPageNumber = (page: number) => {
    setPageNumber(page);
  };

  const getPageSize = (page: number) => {
    setPageSize(page);
    setPageNumber(1);
  };

  useEffect(() => {
    refetch();
  }, [pageNumber]);

  useEffect(() => {
    if (filters?.length && filters[1].length) {
      setPageNumber(1);
    }
  }, [filters]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 1) {
      setAttributesAsin("");
      setSeller("");
      setAttributeData({});
    }
  };

  return (
    <Box sx={CatalogTrackerWrapper}>
      <CatalogTrackerHeader />

      <Box sx={CatalogTrackerContentWrapper}>
        <Box sx={CatalogTrackerTabsWrapper}>
          <Tabs
            value={value}
            onChange={handleChange}
            sx={TabsStyle}
            TabIndicatorProps={{
              hidden: true,
            }}
          >
            <Tab
              label={intl.formatMessage({
                id: I18nKey.SUMMARY_VIEW,
              })}
              disableRipple
              key={0}
              value={0}
              sx={{
                ...TabStyle,
                ...{
                  background:
                    value === 0
                      ? "linear-gradient(90deg, #044F82 0%, #2C96DE 100%)"
                      : "#EEEEEE",
                  borderRadius: "8px",
                  color: value === 0 ? "white !important" : "#757575",
                },
              }}
            />
            <Tab
              label={intl.formatMessage({
                id: I18nKey.ATTRIBUTES,
              })}
              disableRipple
              key={1}
              value={1}
              sx={{
                ...TabStyle,
                ...{
                  background:
                    value === 1
                      ? "linear-gradient(90deg, #044F82 0%, #2C96DE 100%)"
                      : "#EEEEEE",
                  borderRadius: "8px",
                  color: value === 1 ? "white !important" : "#757575",
                },
              }}
            />
          </Tabs>
        </Box>
        <Box sx={{ display: "flex" }}>
          <SellersList />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          margin: "10px 60px 0 0",
        }}
      >
        {value ? (
          <>
            {" "}
            <FormControl>
              <InputLabel sx={DropdownLabelStyle}>Select seller</InputLabel>
              <Select
                sx={{
                  display: value === 0 ? "none" : "inherit",
                  width: "150px",
                }}
                labelId="select-seller-label"
                id="select-seller"
                placeholder="Select Seller"
                value={seller}
                onChange={(e: SelectChangeEvent<string>) => {
                  setSeller(e.target.value);
                }}
                label="Select seller"
              >
                {selectedSellers.map((item) => {
                  return <MenuItem value={item.id}>{item.name}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <TextField
              id="outlined-basic"
              label="Enter ASIN"
              variant="outlined"
              size="small"
              placeholder="Enter ASIN"
              value={attributesAsin}
              onChange={(e) => setAttributesAsin(e.target.value)}
              sx={{
                display: value === 0 ? "none" : "inherit",
                "& .MuiOutlinedInput-root": {
                  background: "rgb(232, 241, 250)",
                },
                "& .MuiInputLabel-root": {
                  background: "rgb(232, 241, 250)",
                  borderRadius: "10px",
                },
                margin: "0px 5px",
              }}
            />
            <Button
              secondary={true}
              primary={false}
              loading={isFetchingCatalogAttributes}
              text={"Apply"}
              margin="0"
              onClick={() => attributeRefetch()}
              width="50px"
              height="25px"
            />
          </>
        ) : null}
      </Box>
      <Box sx={BottomContent}>
        {value === 0 && (
          <CatalogTrackerTable
            rows={mismatchSummaryData}
            isCheckbox={false}
            editableRow={true}
            fetching={isFetching}
            isGrouping={false}
            getColumnToSort={getColumnToSort}
            getPageNumber={getPageNumber}
            getPageSize={getPageSize}
            newPageNumber={pageNumber}
            newPageSize={pageSize}
            source={source}
            setValue={setValue}
            setAttributesAsin={setAttributesAsin}
            setSeller={setSeller}
            attributeRefetch={attributeRefetch}
          />
        )}
        {value === 1 && (
          <CatalogAttributesTable
            data={attributeData}
            rows={[]}
            isCheckbox={false}
            editableRow={true}
            fetching={isFetchingCatalogAttributes}
            isGrouping={false}
            getColumnToSort={getColumnToSort}
            getPageNumber={getPageNumber}
            getPageSize={getPageSize}
            newPageNumber={pageNumber}
            newPageSize={pageSize}
          />
        )}
      </Box>
    </Box>
  );
};

export default CatalogTracker;
